import type { IDocumentAssemblerRenderedError } from '@commenda/api';
import { Badge, Button, Icons, Tooltip } from '@commenda/kakashi';
import { copyToClipboard } from '@commenda/utils';
import { useNotification } from '../hooks/useNotification';

interface DocumentAssemblerFindingCardProps {
  finding: IDocumentAssemblerRenderedError;
  position: number;
  total: number;
}

export const DocumentAssemblerFindingCard = ({
  finding,
  position,
  total,
}: DocumentAssemblerFindingCardProps) => {
  const { setNotification } = useNotification();

  const onParagraphTextCopy = () => {
    const copied = copyToClipboard(finding.paragraphText);
    setNotification({
      show: true,
      valid: copied,
      title: copied ? 'Copied — paste it into Word’s Find' : 'Could not copy',
      message: copied
        ? undefined
        : 'Select the text and copy it manually instead.',
    });
  };

  const hasParagraphText = finding.paragraphText.trim().length > 0;

  return (
    <div className="flex flex-col gap-md rounded-xl border border-border-primary p-xl">
      <div className="flex items-start justify-between gap-md">
        <div className="flex items-start gap-sm">
          <Icons.AlertCircle className="mt-0.5 size-4 shrink-0 text-text-error" />
          <div className="flex flex-col gap-xxs">
            <span className="txt-extra-small-medium text-text-tertiary">
              Problem {position} of {total}
            </span>
            <p className="txt-small-medium text-text-primary">
              {finding.message}
            </p>
          </div>
        </div>
        <Tooltip content="The code we use for this problem internally. Quote it if you ask us about it.">
          <Badge variant="gray" size="sm" className="shrink-0 font-mono">
            {finding.code}
          </Badge>
        </Tooltip>
      </div>

      <div className="flex flex-col gap-sm pl-xl">
        <div className="flex flex-col gap-xxs">
          <span className="txt-extra-small-medium text-text-tertiary">
            Where
          </span>
          <span className="txt-extra-small-regular text-text-secondary">
            {finding.locationDescription}
            {finding.documentPart !== finding.locationDescription && (
              <span className="text-text-tertiary">
                {' · '}
                {finding.documentPart}
              </span>
            )}
          </span>
        </div>

        {hasParagraphText ? (
          <div className="flex flex-col gap-xxs">
            <div className="flex items-center justify-between gap-sm">
              <span className="txt-extra-small-medium text-text-tertiary">
                Search for this in Word
              </span>
              <Button
                variant="link"
                size="xs"
                leftIcon={<Icons.Copy01 className="size-3" />}
                onClick={onParagraphTextCopy}
              >
                Copy
              </Button>
            </div>
            <pre className="txt-extra-small-regular max-h-40 overflow-y-auto rounded-md bg-bg-secondary p-md font-mono whitespace-pre-wrap break-words text-text-secondary">
              {finding.paragraphText}
            </pre>
          </div>
        ) : (
          <p className="txt-extra-small-regular text-text-tertiary">
            This problem is not inside a paragraph, so there is nothing to
            search for. Look for the text below in {finding.documentPart}.
          </p>
        )}

        {finding.offendingText ? (
          <div className="flex flex-col gap-xxs">
            <span className="txt-extra-small-medium text-text-tertiary">
              The text we objected to
            </span>
            <pre className="txt-extra-small-regular max-h-40 overflow-y-auto rounded-md bg-bg-secondary p-md font-mono whitespace-pre-wrap break-all text-text-secondary">
              {finding.offendingText}
            </pre>
          </div>
        ) : null}
      </div>
    </div>
  );
};
